import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { toast } from 'sonner';
import { useAuth } from '../App';
import api from '../services/api';
import { format } from 'date-fns';
import { 
  Bell, 
  Plus,
  Calendar as CalendarIcon,
  Cake,
  Award,
  UserCheck,
  FileText,
  Receipt,
  AlertTriangle,
  Settings2,
  Mail,
  Check,
  X,
  Clock,
  Loader2
} from 'lucide-react';

const reminderTypeConfig = {
  birthday: { icon: Cake, color: 'bg-pink-500', label: 'Birthday' },
  work_anniversary: { icon: Award, color: 'bg-purple-500', label: 'Work Anniversary' },
  probation_end: { icon: UserCheck, color: 'bg-orange-500', label: 'Probation End' },
  visa_expiry: { icon: FileText, color: 'bg-red-500', label: 'Visa Expiry' },
  expense_pending: { icon: Receipt, color: 'bg-amber-500', label: 'Expense Pending' },
  leave_pending: { icon: CalendarIcon, color: 'bg-blue-500', label: 'Leave Pending' },
  contract_end: { icon: FileText, color: 'bg-gray-500', label: 'Contract End' },
  custom: { icon: Bell, color: 'bg-indigo-500', label: 'Custom' },
};

const Reminders = () => {
  const { user } = useAuth();
  const [reminders, setReminders] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dueDate, setDueDate] = useState(null);
  const [newReminder, setNewReminder] = useState({
    reminder_type: 'custom',
    title: '',
    description: '',
    send_email: true,
    send_days_before: 7
  });
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [remindersRes, settingsRes] = await Promise.all([
        api.get('/reminders/upcoming?days=90'),
        api.get('/reminder-settings')
      ]);
      setReminders(remindersRes.data.reminders || []);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Failed to fetch reminders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReminder = async () => {
    if (!newReminder.title || !dueDate) {
      toast.error('Please fill required fields');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/reminders', {
        ...newReminder,
        due_date: format(dueDate, 'yyyy-MM-dd')
      });
      toast.success('Reminder created');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create reminder');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateSettings = async () => {
    setSubmitting(true);
    try {
      await api.put('/reminder-settings', settings);
      toast.success('Settings updated');
      setSettingsDialogOpen(false);
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDismiss = async (reminderId) => {
    try {
      await api.put(`/reminders/${reminderId}/dismiss`);
      toast.success('Reminder dismissed');
      setReminders(prev => prev.filter(r => r.id !== reminderId));
    } catch (error) {
      // For auto-generated reminders, just remove from UI
      setReminders(prev => prev.filter(r => r.id !== reminderId));
    }
  };

  const resetForm = () => {
    setNewReminder({
      reminder_type: 'custom',
      title: '',
      description: '',
      send_email: true,
      send_days_before: 7
    });
    setDueDate(null);
  };

  const getReminderIcon = (type) => {
    const config = reminderTypeConfig[type] || reminderTypeConfig.custom;
    const IconComponent = config.icon;
    return <IconComponent size={18} />;
  };

  const getReminderColor = (type) => {
    const config = reminderTypeConfig[type] || reminderTypeConfig.custom;
    return config.color;
  };

  const getPriorityBadge = (reminder) => {
    if (reminder.priority === 'high' || reminder.days_until <= 3) {
      return <Badge variant="destructive">Urgent</Badge>;
    }
    if (reminder.days_until <= 7) {
      return <Badge variant="secondary">Soon</Badge>;
    }
    return null;
  };

  const filteredReminders = filterType === 'all' 
    ? reminders 
    : reminders.filter(r => r.reminder_type === filterType);

  const groupedReminders = {
    today: filteredReminders.filter(r => r.days_until === 0),
    thisWeek: filteredReminders.filter(r => r.days_until > 0 && r.days_until <= 7),
    thisMonth: filteredReminders.filter(r => r.days_until > 7 && r.days_until <= 30),
    later: filteredReminders.filter(r => r.days_until > 30 || r.days_pending)
  };

  const canManage = ['super_admin', 'hr_admin'].includes(user?.role);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Reminders</h1>
          <p className="text-muted-foreground">
            Track birthdays, probations, visa expiries, and pending approvals
          </p>
        </div>
        <div className="flex gap-2">
          {canManage && (
            <>
              <Button variant="outline" onClick={() => setSettingsDialogOpen(true)} data-testid="reminder-settings-btn">
                <Settings2 size={18} className="mr-2" />
                Settings
              </Button>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-hover" data-testid="add-reminder-btn">
                    <Plus size={18} className="mr-2" />
                    Add Reminder
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create Custom Reminder</DialogTitle>
                    <DialogDescription>
                      Set up a custom reminder for any HR event
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Reminder Type</Label>
                      <Select 
                        value={newReminder.reminder_type} 
                        onValueChange={(v) => setNewReminder({ ...newReminder, reminder_type: v })}
                      >
                        <SelectTrigger data-testid="reminder-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(reminderTypeConfig).map(([key, config]) => (
                            <SelectItem key={key} value={key}>
                              <span className="flex items-center gap-2">
                                <config.icon size={16} />
                                {config.label}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Title</Label>
                      <Input
                        placeholder="Reminder title..."
                        value={newReminder.title}
                        onChange={(e) => setNewReminder({ ...newReminder, title: e.target.value })}
                        data-testid="reminder-title"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Due Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="reminder-date-btn">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {dueDate ? format(dueDate, 'PPP') : 'Select date'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={dueDate}
                            onSelect={setDueDate}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="space-y-2">
                      <Label>Description (Optional)</Label>
                      <Textarea
                        placeholder="Additional details..."
                        value={newReminder.description}
                        onChange={(e) => setNewReminder({ ...newReminder, description: e.target.value })}
                        data-testid="reminder-description"
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Switch 
                          checked={newReminder.send_email} 
                          onCheckedChange={(v) => setNewReminder({ ...newReminder, send_email: v })}
                          data-testid="send-email-switch"
                        />
                        <Label>Send Email Notification</Label>
                      </div>
                    </div>

                    {newReminder.send_email && (
                      <div className="space-y-2">
                        <Label>Send reminder (days before)</Label>
                        <Input
                          type="number"
                          min="0"
                          value={newReminder.send_days_before}
                          onChange={(e) => setNewReminder({ ...newReminder, send_days_before: parseInt(e.target.value) || 0 })}
                          data-testid="days-before-input"
                        />
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                      Cancel
                    </Button>
                    <Button onClick={handleCreateReminder} disabled={submitting} data-testid="save-reminder-btn">
                      {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Create Reminder
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-hover cursor-pointer" onClick={() => setFilterType('all')} data-testid="filter-all">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Bell className="text-primary" size={20} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-2xl font-bold">{reminders.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover cursor-pointer" onClick={() => setFilterType('birthday')} data-testid="filter-birthday">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-pink-500/10 flex items-center justify-center">
                <Cake className="text-pink-500" size={20} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Birthdays</p>
                <p className="text-2xl font-bold">
                  {reminders.filter(r => r.reminder_type === 'birthday').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover cursor-pointer" onClick={() => setFilterType('probation_end')} data-testid="filter-probation">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-orange-500/10 flex items-center justify-center">
                <UserCheck className="text-orange-500" size={20} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Probations</p>
                <p className="text-2xl font-bold">
                  {reminders.filter(r => r.reminder_type === 'probation_end').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover cursor-pointer" onClick={() => setFilterType('expense_pending')} data-testid="filter-expenses">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                <Receipt className="text-amber-500" size={20} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold">
                  {reminders.filter(r => r.reminder_type === 'expense_pending' || r.reminder_type === 'leave_pending').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      {filterType !== 'all' && (
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-sm">
            Filtering: {reminderTypeConfig[filterType]?.label || filterType}
          </Badge>
          <Button variant="ghost" size="sm" onClick={() => setFilterType('all')}>
            Clear filter
          </Button>
        </div>
      )}

      {/* Reminders List */}
      <div className="space-y-6">
        {/* Today */}
        {groupedReminders.today.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <AlertTriangle className="text-red-500" size={20} />
              Today
            </h3>
            <div className="space-y-3">
              {groupedReminders.today.map(reminder => (
                <ReminderCard 
                  key={reminder.id} 
                  reminder={reminder} 
                  onDismiss={handleDismiss}
                  getReminderIcon={getReminderIcon}
                  getReminderColor={getReminderColor}
                  getPriorityBadge={getPriorityBadge}
                />
              ))}
            </div>
          </div>
        )}

        {/* This Week */}
        {groupedReminders.thisWeek.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Clock className="text-amber-500" size={20} />
              This Week
            </h3>
            <div className="space-y-3">
              {groupedReminders.thisWeek.map(reminder => (
                <ReminderCard 
                  key={reminder.id} 
                  reminder={reminder} 
                  onDismiss={handleDismiss}
                  getReminderIcon={getReminderIcon}
                  getReminderColor={getReminderColor}
                  getPriorityBadge={getPriorityBadge}
                />
              ))}
            </div>
          </div>
        )}

        {/* This Month */}
        {groupedReminders.thisMonth.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <CalendarIcon className="text-blue-500" size={20} />
              This Month
            </h3>
            <div className="space-y-3">
              {groupedReminders.thisMonth.map(reminder => (
                <ReminderCard 
                  key={reminder.id} 
                  reminder={reminder} 
                  onDismiss={handleDismiss}
                  getReminderIcon={getReminderIcon}
                  getReminderColor={getReminderColor}
                  getPriorityBadge={getPriorityBadge}
                />
              ))}
            </div>
          </div>
        )}

        {/* Later / Pending */}
        {groupedReminders.later.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Bell className="text-muted-foreground" size={20} />
              Later / Pending Actions
            </h3>
            <div className="space-y-3">
              {groupedReminders.later.map(reminder => (
                <ReminderCard 
                  key={reminder.id} 
                  reminder={reminder} 
                  onDismiss={handleDismiss}
                  getReminderIcon={getReminderIcon}
                  getReminderColor={getReminderColor}
                  getPriorityBadge={getPriorityBadge}
                />
              ))}
            </div>
          </div>
        )}

        {filteredReminders.length === 0 && (
          <Card>
            <CardContent className="p-12 text-center">
              <Bell className="mx-auto text-muted-foreground mb-4" size={48} />
              <p className="text-muted-foreground">No reminders found</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Settings Dialog */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reminder Settings</DialogTitle>
            <DialogDescription>
              Configure when reminders are triggered
            </DialogDescription>
          </DialogHeader>
          {settings && (
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between">
                <Label>Email Notifications</Label>
                <Switch 
                  checked={settings.email_enabled} 
                  onCheckedChange={(v) => setSettings({ ...settings, email_enabled: v })}
                />
              </div>
              <p className="text-xs text-muted-foreground">Note: Email is mocked for MVP</p>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Probation Reminder (days before)</Label>
                  <Input
                    type="number"
                    value={settings.probation_reminder_days}
                    onChange={(e) => setSettings({ ...settings, probation_reminder_days: parseInt(e.target.value) || 7 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Birthday Reminder (days before)</Label>
                  <Input
                    type="number"
                    value={settings.birthday_reminder_days}
                    onChange={(e) => setSettings({ ...settings, birthday_reminder_days: parseInt(e.target.value) || 1 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Anniversary Reminder (days before)</Label>
                  <Input
                    type="number"
                    value={settings.anniversary_reminder_days}
                    onChange={(e) => setSettings({ ...settings, anniversary_reminder_days: parseInt(e.target.value) || 7 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Visa Expiry Reminder (days before)</Label>
                  <Input
                    type="number"
                    value={settings.visa_expiry_reminder_days}
                    onChange={(e) => setSettings({ ...settings, visa_expiry_reminder_days: parseInt(e.target.value) || 30 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Expense Pending (days after)</Label>
                  <Input
                    type="number"
                    value={settings.expense_pending_reminder_days}
                    onChange={(e) => setSettings({ ...settings, expense_pending_reminder_days: parseInt(e.target.value) || 3 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Leave Pending (days after)</Label>
                  <Input
                    type="number"
                    value={settings.leave_pending_reminder_days}
                    onChange={(e) => setSettings({ ...settings, leave_pending_reminder_days: parseInt(e.target.value) || 2 })}
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateSettings} disabled={submitting}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ReminderCard = ({ reminder, onDismiss, getReminderIcon, getReminderColor, getPriorityBadge }) => {
  return (
    <Card className="card-hover" data-testid={`reminder-${reminder.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className={`h-10 w-10 rounded-full ${getReminderColor(reminder.reminder_type)} bg-opacity-10 flex items-center justify-center flex-shrink-0`}>
            <span className={getReminderColor(reminder.reminder_type).replace('bg-', 'text-')}>
              {getReminderIcon(reminder.reminder_type)}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-semibold">{reminder.title}</h4>
              {getPriorityBadge(reminder)}
              <Badge variant="outline" className="text-xs">
                {reminderTypeConfig[reminder.reminder_type]?.label || 'Reminder'}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{reminder.description}</p>
            {reminder.employee_name && (
              <p className="text-sm mt-1">
                <span className="text-muted-foreground">Employee:</span>{' '}
                <span className="font-medium">{reminder.employee_name}</span>
              </p>
            )}
            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <CalendarIcon size={12} />
                {reminder.due_date}
              </span>
              {reminder.days_until !== undefined && reminder.days_until >= 0 && (
                <span className={`font-medium ${reminder.days_until <= 3 ? 'text-red-500' : reminder.days_until <= 7 ? 'text-amber-500' : ''}`}>
                  {reminder.days_until === 0 ? 'Today' : `${reminder.days_until} days away`}
                </span>
              )}
              {reminder.days_pending !== undefined && (
                <span className="text-amber-500 font-medium">
                  Pending for {reminder.days_pending} days
                </span>
              )}
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => onDismiss(reminder.id)}
            className="flex-shrink-0"
            data-testid={`dismiss-${reminder.id}`}
          >
            <X size={16} />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default Reminders;
